import bindAll from 'lodash.bindall';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Modal from '../../containers/modal.jsx';
import {API_DOMAIN, http} from '../../lib/http/request';
import {loadDictOption} from '../../lib/dict-service';

import styles from './project-gallery.css';
import {HashPrefix} from '../../lib/hash-prefix';
import collectIcon from './collect-0.svg';
import collectIconActive from './collect-1.svg';
import praiseIcon from './praise-icon-0.svg';
import praiseIconActive from './praise-icon-1.svg';
import previewIcon from './preview.svg';

const API_ORIGIN = API_DOMAIN.replace(/\/api\/?$/, '');
const PAGE_SIZE = 12;
const PROJECT_TAG_DICT_CODE = 'scratch_project_tag';

const SORT_OPTIONS = [
    {
        label: '最新',
        value: ''
    },
    {
        label: '点赞量',
        value: 'likeCount'
    },
    {
        label: '收藏量',
        value: 'favoriteCount'
    }
];

const getImageSrc = image => {
    if (!image) return '';
    if (/^(data:|blob:|https?:)/.test(image)) return image;
    if (image.charAt(0) === '/') return `${API_ORIGIN}${image}`;
    return image;
};

const formatDate = value => {
    if (!value) return '暂无时间';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

const normalizePageResult = response => {
    const page = response && response.result ? response.result : {};
    return {
        records: Array.isArray(page.records) ? page.records : [],
        total: Number(page.total || 0),
        pageIndex: Number(page.current || 1),
        pageSize: Number(page.size || PAGE_SIZE)
    };
};

const normalizeProjectTags = tags => {
    if (Array.isArray(tags)) {
        return tags.map(tag => String(tag || '').trim()).filter(Boolean);
    }
    return String(tags || '')
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean);
};

const normalizeTagOptions = items => (
    (Array.isArray(items) ? items : [])
        .map(item => {
            const value = item.itemValue || item.value || item.id || '';
            const label = item.itemText || item.label || item.name || value;
            return {
                color: item.itemColor || '',
                label,
                value
            };
        })
        .filter(item => item.value && item.label)
);

const getCountText = value => String(Number(value || 0));

const getPreviewUrl = project => {
    return `/player.html#${HashPrefix.PROJECT}${project.id}`;
};

class ProjectGallery extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleCategoryChange',
            'handleFavorite',
            'handleLike',
            'handleLoadMore',
            'handleNextPage',
            'handleOpenProject',
            'handlePrevPage',
            'handlePreviewProject',
            'handleRefresh',
            'handleScroll',
            'handleSortChange',
            'handleTouchEnd',
            'handleTouchMove',
            'handleTouchStart',
            'loadProjectTags',
            'loadProjects'
        ]);
        this.state = {
            actionProjectId: '',
            categoryError: '',
            error: '',
            hasMore: true,
            loadingCategories: false,
            loading: false,
            pageIndex: 1,
            pageSize: PAGE_SIZE,
            projects: [],
            projectTagOptions: [],
            pullDistance: 0,
            refreshing: false,
            selectedTag: '',
            sortType: '',
            total: 0
        };
        this.mounted = false;
        this.pullStartY = null;
    }
    componentDidMount () {
        this.mounted = true;
        this.loadProjectTags();
        this.loadProjects(1);
    }
    componentWillUnmount () {
        this.mounted = false;
    }
    loadProjects (pageIndex, append) {
        const params = {
            pageIndex,
            pageSize: this.state.pageSize
        };
        if (this.state.sortType) {
            params.sortType = this.state.sortType;
        }
        if (this.state.selectedTag) {
            params.tags = this.state.selectedTag;
        }
        this.setState({
            error: '',
            loading: true,
            refreshing: !append && pageIndex === 1
        });
        http.get('/scratchProject/openPageList', params)
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '开放作品加载失败');
                }
                const page = normalizePageResult(response);
                const hasMore = page.total ?
                    page.pageIndex * page.pageSize < page.total :
                    page.records.length >= page.pageSize;
                this.setState(prevState => ({
                    hasMore,
                    pageIndex: page.pageIndex,
                    pageSize: page.pageSize,
                    projects: append ? prevState.projects.concat(page.records) : page.records,
                    total: page.total
                }));
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState(prevState => ({
                    error: error.message || '开放作品加载失败',
                    hasMore: false,
                    projects: append ? prevState.projects : [],
                    total: append ? prevState.total : 0
                }));
            })
            .then(() => {
                if (!this.mounted) return;
                this.setState({
                    loading: false,
                    pullDistance: 0,
                    refreshing: false
                });
            });
    }
    loadProjectTags () {
        this.setState({
            categoryError: '',
            loadingCategories: true
        });
        loadDictOption(PROJECT_TAG_DICT_CODE)
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '分类加载失败');
                }
                this.setState({
                    loadingCategories: false,
                    projectTagOptions: normalizeTagOptions(response.result)
                });
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    categoryError: error.message || '分类加载失败',
                    loadingCategories: false,
                    projectTagOptions: []
                });
            });
    }
    handleCategoryChange (event) {
        const selectedTag = event.currentTarget.getAttribute('data-tag-value') || '';
        if (selectedTag === this.state.selectedTag || this.state.loading) return;
        this.setState({selectedTag}, () => {
            this.loadProjects(1);
        });
    }
    handleSortChange (event) {
        const sortType = event.currentTarget.getAttribute('data-sort-type') || '';
        if (sortType === this.state.sortType || this.state.loading) return;
        this.setState({sortType}, () => {
            this.loadProjects(1);
        });
    }
    handlePrevPage () {
        if (this.state.pageIndex <= 1 || this.state.loading) return;
        this.loadProjects(this.state.pageIndex - 1);
    }
    handleNextPage () {
        const pageCount = Math.max(1, Math.ceil(this.state.total / this.state.pageSize));
        if (this.state.pageIndex >= pageCount || this.state.loading) return;
        this.loadProjects(this.state.pageIndex + 1);
    }
    handleRefresh () {
        if (this.state.loading) return;
        this.loadProjects(1);
    }
    handleLoadMore () {
        if (this.state.loading || !this.state.hasMore) return;
        this.loadProjects(this.state.pageIndex + 1, true);
    }
    handleScroll (event) {
        if (this.state.loading || !this.state.hasMore) return;
        const {scrollTop, scrollHeight, clientHeight} = event.currentTarget;
        if (scrollHeight - scrollTop - clientHeight <= 160) {
            this.handleLoadMore();
        }
    }
    handleTouchStart (event) {
        if (event.currentTarget.scrollTop > 0 || event.touches.length !== 1) return;
        this.pullStartY = event.touches[0].clientY;
    }
    handleTouchMove (event) {
        if (this.pullStartY === null || event.touches.length !== 1) return;
        const deltaY = event.touches[0].clientY - this.pullStartY;
        if (deltaY <= 0) {
            this.setState({pullDistance: 0});
            return;
        }
        this.setState({pullDistance: Math.min(deltaY, 80)});
    }
    handleTouchEnd () {
        const shouldRefresh = this.state.pullDistance >= 56 && !this.state.loading;
        this.pullStartY = null;
        if (shouldRefresh) {
            this.loadProjects(1);
            return;
        }
        this.setState({pullDistance: 0});
    }
    handleOpenProject (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        if (!projectId) return;
        window.location.hash = HashPrefix.PROJECT + projectId;
        this.props.onRequestClose();
    }
    handlePreviewProject (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        const project = this.state.projects.find(item => item.id === projectId);
        const previewUrl = getPreviewUrl(project);
        if (!previewUrl) return;
        const previewWindow = window.open(previewUrl, '_blank', 'noopener,noreferrer');
        if (previewWindow) {
            previewWindow.opener = null;
        }
    }
    updateProjectAction (projectId, url, fallbackMessage) {
        if (!projectId || this.state.actionProjectId) return;
        this.setState({
            actionProjectId: projectId,
            error: ''
        });
        http.post(url)
            .then(response => {
                if (!this.mounted) return;
                if (response && response.success === false) {
                    throw new Error(response.message || fallbackMessage);
                }
                const updatedProject = response && response.result ? response.result : null;
                if (!updatedProject) {
                    throw new Error(fallbackMessage);
                }
                this.setState(prevState => ({
                    actionProjectId: '',
                    projects: prevState.projects.map(project => (
                        project.id === updatedProject.id ? updatedProject : project
                    ))
                }));
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    actionProjectId: '',
                    error: error.message || fallbackMessage
                });
            });
    }
    handleLike (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        this.updateProjectAction(projectId, `/scratchProject/like/${projectId}`, '点赞操作失败');
    }
    handleFavorite (event) {
        const projectId = event.currentTarget.getAttribute('data-project-id');
        this.updateProjectAction(projectId, `/scratchProject/favorite/${projectId}`, '收藏操作失败');
    }
    getTagLabel (tagValue) {
        const option = this.state.projectTagOptions.find(item => item.value === tagValue);
        return option ? option.label : tagValue;
    }
    renderProjectTags (project) {
        const tags = normalizeProjectTags(project.tags);
        if (tags.length === 0) return null;
        const visibleTags = tags.slice(0, 3);
        return (
            <div className={styles.projectTags}>
                {visibleTags.map(tag => (
                    <span
                        className={styles.projectTag}
                        key={tag}
                    >
                        {this.getTagLabel(tag)}
                    </span>
                ))}
                {tags.length > visibleTags.length ? (
                    <span className={styles.projectTagMore}>
                        {`+${tags.length - visibleTags.length}`}
                    </span>
                ) : null}
            </div>
        );
    }
    renderCategoryFilter () {
        const options = [{
            color: '',
            label: '全部',
            value: ''
        }].concat(this.state.projectTagOptions);
        return (
            <div className={styles.categoryFilter}>
                <span className={styles.categoryLabel}>{'项目分类'}</span>
                <div className={styles.categoryOptions}>
                    {options.map(option => {
                        const active = this.state.selectedTag === option.value;
                        const colorStyle = option.color ? {backgroundColor: option.color} : {};
                        return (
                            <button
                                className={classNames(styles.categoryButton, {
                                    [styles.categoryButtonActive]: active
                                })}
                                data-tag-value={option.value}
                                disabled={this.state.loading}
                                key={option.value || 'all'}
                                type="button"
                                onClick={this.handleCategoryChange}
                            >
                                {option.color ? (
                                    <span
                                        className={styles.categoryColor}
                                        style={colorStyle}
                                    />
                                ) : null}
                                <span>{option.label}</span>
                            </button>
                        );
                    })}
                </div>
                {this.state.loadingCategories || this.state.categoryError ? (
                    <span className={styles.categoryStatus}>
                        {this.state.loadingCategories ? '正在读取分类' : this.state.categoryError}
                    </span>
                ) : null}
            </div>
        );
    }
    renderProjectCard (project) {
        const screenshot = getImageSrc(project.screenshot);
        const projectName = project.projectName || '未命名作品';
        const actionBusy = this.state.actionProjectId === project.id;
        return (
            <article
                className={styles.galleryCard}
                key={project.id}
            >
                <button
                    className={styles.coverButton}
                    data-project-id={project.id}
                    type="button"
                    onClick={this.handleOpenProject}
                >
                    {screenshot ? (
                        <img
                            className={styles.coverImage}
                            src={screenshot}
                        />
                    ) : (
                        <span className={styles.coverFallback}>
                            {projectName.charAt(0)}
                        </span>
                    )}
                </button>
                <div className={styles.cardBody}>
                    <div className={styles.projectName}>{projectName}</div>
                    <div className={styles.projectMeta}>
                        {`更新于 ${formatDate(project.updateTime || project.createTime)}`}
                    </div>
                    {this.renderProjectTags(project)}
                    <div className={styles.cardFooter}>
                        <div className={styles.reactionGroup}>
                            <button
                                aria-label={project.liked ? '取消点赞' : '点赞'}
                                className={classNames(styles.actionButton, {
                                    [styles.actionButtonActive]: project.liked
                                })}
                                data-project-id={project.id}
                                disabled={actionBusy}
                                title={project.liked ? '取消点赞' : '点赞'}
                                type="button"
                                onClick={this.handleLike}
                            >
                                <img
                                    className={styles.actionIcon}
                                    src={project.liked ? praiseIconActive : praiseIcon}
                                />
                                <span className={styles.actionCount}>{getCountText(project.likeCount)}</span>
                            </button>
                            <button
                                aria-label={project.favorited ? '取消收藏' : '收藏'}
                                className={classNames(styles.actionButton, {
                                    [styles.actionButtonActive]: project.favorited
                                })}
                                data-project-id={project.id}
                                disabled={actionBusy}
                                title={project.favorited ? '取消收藏' : '收藏'}
                                type="button"
                                onClick={this.handleFavorite}
                            >
                                <img
                                    className={styles.actionIcon}
                                    src={project.favorited ? collectIconActive : collectIcon}
                                />
                                <span className={styles.actionCount}>{getCountText(project.favoriteCount)}</span>
                            </button>
                        </div>
                        <div className={styles.cardActions}>
                            <button
                                aria-label={`预览 ${projectName}`}
                                className={styles.previewButton}
                                data-project-id={project.id}
                                disabled={!project.filePath}
                                title={project.filePath ? '预览' : '暂无预览文件'}
                                type="button"
                                onClick={this.handlePreviewProject}
                            >
                                <img
                                    className={styles.previewIcon}
                                    src={previewIcon}
                                />
                                <span>{'预览'}</span>
                            </button>
                            <button
                                className={styles.openButton}
                                data-project-id={project.id}
                                type="button"
                                onClick={this.handleOpenProject}
                            >
                                {'打开'}
                            </button>
                        </div>
                    </div>
                </div>
            </article>
        );
    }
    render () {
        const pullActive = this.state.pullDistance >= 56;
        return (
            <Modal
                fullScreen
                contentLabel="开放作品"
                id="project-gallery"
                onRequestClose={this.props.onRequestClose}
            >
                <section className={styles.projectGallery}>
                    <div className={styles.galleryHeader}>
                        <div>
                            <div className={styles.galleryTitle}>{'开放作品'}</div>
                            <div className={styles.gallerySummary}>
                                {this.state.loading ? '正在读取作品' : `共 ${this.state.total} 个作品`}
                            </div>
                        </div>
                        <div className={styles.galleryControls}>
                            <div className={styles.sortControl}>
                                {SORT_OPTIONS.map(option => (
                                    <button
                                        className={classNames(styles.sortButton, {
                                            [styles.sortButtonActive]: this.state.sortType === option.value
                                        })}
                                        data-sort-type={option.value}
                                        key={option.value || 'latest'}
                                        type="button"
                                        onClick={this.handleSortChange}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                className={styles.refreshButton}
                                disabled={this.state.loading}
                                type="button"
                                onClick={this.handleRefresh}
                            >
                                {'刷新'}
                            </button>
                        </div>
                    </div>
                    {this.renderCategoryFilter()}
                    <div
                        className={styles.galleryScroll}
                        onScroll={this.handleScroll}
                        onTouchEnd={this.handleTouchEnd}
                        onTouchMove={this.handleTouchMove}
                        onTouchStart={this.handleTouchStart}
                    >
                        {this.state.pullDistance > 0 ? (
                            <div
                                className={styles.pullStatus}
                                style={{height: `${this.state.pullDistance}px`}}
                            >
                                {pullActive ? '松开刷新' : '下拉刷新'}
                            </div>
                        ) : null}
                        {this.state.error ? (
                            <div className={styles.galleryError}>{this.state.error}</div>
                        ) : null}
                        {!this.state.loading && !this.state.error && this.state.projects.length === 0 ? (
                            <div className={styles.galleryEmpty}>
                                <div className={styles.emptyTitle}>{'暂无开放作品'}</div>
                                <div className={styles.emptyText}>{'设置为公开展示的作品会出现在这里。'}</div>
                            </div>
                        ) : null}
                        {this.state.projects.length > 0 ? (
                            <div className={styles.galleryGrid}>
                                {this.state.projects.map(project => this.renderProjectCard(project))}
                            </div>
                        ) : null}
                        <div className={styles.loadMoreStatus}>
                            {this.state.loading ?
                                (this.state.refreshing ? '正在刷新' : '正在加载更多') :
                                (this.state.projects.length > 0 ?
                                    (this.state.hasMore ? '上滑加载更多' : '没有更多作品了') :
                                    '')}
                        </div>
                    </div>
                </section>
            </Modal>
        );
    }
}

ProjectGallery.propTypes = {
    onRequestClose: PropTypes.func
};

ProjectGallery.defaultProps = {
    onRequestClose: () => {}
};

export default ProjectGallery;
